import { Styles } from 'react-modal'
import { IconNotebook } from '@centivo/react-components'
import { isArray, isString } from 'lodash'

export const checkHasPracticePhone = (practicePhone: string) =>
  practicePhone && practicePhone !== '(   )   -'

export const checkHasPracticeFax = (practiceFax: string | undefined | null) =>
  practiceFax && practiceFax !== '(   )   -'

export const checkHasPracticeContact = (
  practicePhone: string,
  practiceFax: string | undefined | null
) => checkHasPracticeFax(practiceFax) || checkHasPracticePhone(practicePhone)

export const checkIsVirtualCare = (virtualOption: string) =>
  virtualOption && virtualOption.toLocaleLowerCase() !== 'n'

export const checkIsVirtualCareOnly = (virtualOption: string) =>
  virtualOption && virtualOption.toLocaleLowerCase() === 'only'

export const checkMultipleLanguages = (languages: string) => {
  const languagesList: Array<{ text: string }> = []

  if (languages.includes(',')) {
    languages.split(',').forEach(language => {
      languagesList.push({
        text: language,
      })
    })

    return languagesList
  }

  return languages
}

export const getModalStyle = (
  isMobileView: boolean,
  isSlice?: boolean
): Styles => {
  const defaultContent = {
    padding: '16px',
    width: 'max-content',
    maxWidth: '335px',
  }

  const sliceContent = {
    padding: '24px 20px',
    backgroundColor: '#F7F4E6',
    borderRadius: '20px',
  }

  return isMobileView
    ? {
        content: {
          height: `${window.innerHeight}px`,
          width: '100%',
          zIndex: 100,
          margin: 0,
          inset: 0,
          backgroundColor: isSlice ? '#F7F4E6' : 'rgb(255, 255, 255)',
        },
      }
    : {
        content: isSlice
          ? { ...defaultContent, ...sliceContent }
          : defaultContent,
      }
}

export const sanitizeUrl = (url?: string | null) => {
  if (!url) return ''

  if (!url?.includes('https://') && !url?.includes('http://')) {
    url = `http://${url}`
  }

  return url.replace('www.', '')
}

export const locationData = (
  isVirtualCareOnly: boolean,
  isVirtualCare: boolean,
  practitionerDetails: any
) => {
  const list = [] as { text: string; icon?: IconNotebook; type?: string }[]
  const {
    address1,
    address2,
    city,
    state,
    zipCode,
    practicePhone,
    practiceFax,
  } = practitionerDetails

  if (!isVirtualCareOnly) {
    list.push({
      text: `${address1}${
        address2 ? `, ${address2}` : ''
      } ${city}, ${state}, ${zipCode}`,
      icon: 'slice-map-pin-fill',
    })
  }

  if (isVirtualCare) {
    list.push({
      text: isVirtualCareOnly ? 'Virtual care only' : 'Virtual care available',
      icon: 'slice-video-fill',
    })
  }

  if (checkHasPracticeContact(practicePhone, practiceFax)) {
    if (checkHasPracticePhone(practicePhone)) {
      list.push({
        text: practicePhone,
        icon: 'slice-phone-fill',
        type: 'contact',
      })
    }

    if (checkHasPracticeFax(practiceFax)) {
      list.push({
        text: practiceFax,
        icon: 'slice-fax-fill',
        type: 'contact',
      })
    }
  }

  return list as { text: string; icon?: IconNotebook; type?: string }[]
}

const educationsText = (educations: any) =>
  isArray(educations)
    ? [
        ...new Set(
          educations.map(education =>
            isString(education) ? { text: education } : { text: education.name }
          )
        ),
      ]
    : educations ?? ''

const getGender = (gender: string) => {
  switch (gender?.toLowerCase()?.charAt(0)) {
    case 'f':
      return 'Female'
    case 'm':
      return 'Male'
    default:
      return 'Unknown'
  }
}

export const aboutData = (practitionerDetails: any) => {
  const {
    primarySpecialty,
    taxonomies,
    subSpecialty,
    isAcceptingNewPatients,
    languages,
    hospitalPrivlidges: hospitalPrivileges,
    educations,
    gender,
    boardName,
    npi,
  } = practitionerDetails

  return [
    {
      title: 'Specialties',
      value:
        (taxonomies ?? []).map((taxonomy: { taxonomyDescription: any }) => ({
          text: taxonomy?.taxonomyDescription,
        })) ?? [],
    },
    {
      title: 'Accepting New Patients',
      value: isAcceptingNewPatients ? 'Yes' : 'No',
    },
    {
      show: !!languages,
      title: 'Languages',
      value: languages,
    },
    {
      show: !!hospitalPrivileges,
      title: 'Hospital affiliations',
      value: hospitalPrivileges as string,
    },
    {
      show: !!educations,
      title: 'Education and Training',
      value: educationsText(educations),
    },
    {
      title: 'Gender',
      value: getGender(gender),
    },
    {
      show: !!boardName,
      title: 'Board Certifications',
      value: boardName,
    },
    {
      show: !!npi,
      title: 'NPI Number',
      value: npi,
    },
    {
      show: true,
      title: 'In-network',
      value: 'Yes',
    },
  ]
}
